import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'face-collections', label: 'Face Collections sidebar' },
  { id: 'named', label: 'Named People' },
  { id: 'unnamed', label: 'Unnamed People' },
  { id: 'person-card', label: 'Person card' },
  { id: 'cluster-card', label: 'Cluster card' },
  { id: 'maintenance', label: 'Maintenance tools' },
];

export default function Body() {
  return (
    <>
      <DocSection id="face-collections" title="Face Collections sidebar">
        <p>
          The <strong>People</strong> tab turns every face ClipRadar detects into something you can
          search by name. A sidebar lists your <strong>Face Collections</strong> — hand-picked groups
          of people, like "Interview subjects" or "Crew" — next to the{' '}
          <strong>People board</strong> itself. In the Premiere panel, the sidebar collapses to a
          compact dropdown.
        </p>
        <DocShot
          slug="people-tab"
          shot="board"
          alt="The People board with Named People and Unnamed People sections"
          caption="The People board: Named People above, Unnamed People (clusters to verify) below."
          callouts={[
            { n: 1, label: 'Face Collections sidebar — hand-picked groups of people' },
            { n: 2, label: 'Named People — person cards you can already search by name' },
            { n: 3, label: 'Unnamed People — clusters waiting to be named or dismissed' },
            { n: 4, label: 'Maintenance header — Rebuild Groups, Delete Unnamed, Refresh, search by name' },
          ]}
        />
        <p>
          Faces don't start out as people — they start as automatically grouped{' '}
          <strong>clusters</strong>. Naming a cluster is what turns it into a <strong>person</strong>{' '}
          you can filter search by; grouping people into a Face Collection is a separate, optional
          organizational step from either card's menu.
        </p>
      </DocSection>

      <DocSection id="named" title="Named People">
        <p>
          A dense grid of person cards — everyone you've already named. Once someone is here, ClipRadar
          finds them in every clip: type their name in Search, or pick them from the{' '}
          <Link to="/docs/interface/search">People filter</Link> or an{' '}
          <Link to="/docs/interface/search#mention">@-mention</Link>. Before you've named anyone, this
          section is empty — start in <strong>Unnamed People</strong> below.
        </p>
      </DocSection>

      <DocSection id="unnamed" title="Unnamed People">
        <p>
          Wider cards for verification — every cluster ClipRadar has grouped automatically but you
          haven't named yet. Click a card to select it, Shift+click for a range, or right-click for
          merge and other actions. <span style={kbd}>Delete</span> dismisses a cluster (it may
          regroup later as more faces are analyzed); <span style={kbd}>Ignore</span> permanently
          blacklists those faces so they stop appearing at all.
        </p>
        <DocShot
          slug="people-tab"
          shot="cluster-card"
          alt="An unnamed cluster card with its Add name field and suggested-person badge"
        />
        <p>
          A <span style={kbd}>Show more</span> button pages through additional clusters. The server
          caps this list at the <strong>2,000 largest groups</strong> — on a library with an unusually
          large number of distinct faces, the smallest clusters won't appear here until larger ones are
          named or dismissed. If the section is empty, select some media and run{' '}
          <span style={kbd}>Find Faces</span> from the{' '}
          <Link to="/docs/interface/media#row-menu">Media row menu</Link> first — see{' '}
          <Link to="/docs/guides/find-and-name-people">Find & name people</Link> for the full
          walkthrough.
        </p>
      </DocSection>

      <DocSection id="person-card" title="Person card">
        <p>
          A person card shows a face crop and appearance count, with an inline pencil to rename them —{' '}
          <span style={kbd}>Enter</span> or clicking away saves it. Any Face Collections they belong to
          show as chips on the card.
        </p>
        <DocShot
          slug="people-tab"
          shot="person-card-menu"
          alt="A person card with its menu open, showing Inspect faces, Review matching faces, Add to category, Merge and Delete"
        />
        <p>
          Its menu offers <span style={kbd}>Inspect faces</span> (see every face grouped under this
          person), <span style={kbd}>Review matching faces</span> (searches unnamed clusters for faces
          that look like this person and opens a review banner to confirm each one),{' '}
          <span style={kbd}>Add to category…</span> (file them into a Face Collection),{' '}
          <span style={kbd}>Merge…</span>, and <span style={kbd}>Delete…</span>. Select multiple person
          cards for bulk <span style={kbd}>Merge N</span> or <span style={kbd}>Delete N</span>.
        </p>
      </DocSection>

      <DocSection id="cluster-card" title="Cluster card">
        <p>
          A cluster card shows up to two face thumbnails, a count, and — when ClipRadar recognizes a
          likely match — a suggested-person badge. An inline{' '}
          <span style={kbd}>Add name…</span> field turns the cluster into a person on{' '}
          <span style={kbd}>Enter</span> or click-away; typing a name that already exists folds the
          cluster into that existing person instead of creating a duplicate.
        </p>
        <p>
          Its menu offers <span style={kbd}>Inspect faces</span>,{' '}
          <span style={kbd}>Add to person…</span> (fold it into someone already named),{' '}
          <span style={kbd}>Delete…</span> (dismiss — it may regroup later), and{' '}
          <span style={kbd}>Ignore permanently…</span> (blacklist these faces for good). Select
          multiple cluster cards for bulk <span style={kbd}>Add N groups to person</span> or{' '}
          <span style={kbd}>Delete N groups</span>.
        </p>
      </DocSection>

      <DocSection id="maintenance" title="Maintenance tools">
        <p>
          The board header (it collapses as you scroll) carries tools for the whole board rather than
          one card:
        </p>
        <DocShot
          slug="people-tab"
          shot="maintenance"
          alt="A maintenance-tool confirm dialog, type-to-confirm before deleting unnamed groups"
        />
        <ul>
          <li>
            <span style={kbd}>Rebuild Groups</span> — regroups every detected face from scratch. Named
            people are kept; faces you'd previously deleted (but not ignored) can come back.
          </li>
          <li>
            <span style={kbd}>Delete Unnamed</span> — clears every unnamed cluster. Type-to-confirm,
            since it's a bulk, hard-to-undo action.
          </li>
          <li>
            <span style={kbd}>Reset ignored faces</span> — appears only when at least one face has been
            ignored; clears the blacklist so those faces can be grouped again.
          </li>
          <li>
            <span style={kbd}>Refresh</span> and a <span style={kbd}>Search by name</span> field round
            out the header.
          </li>
        </ul>
        <Callout kind="warn">
          A review banner appears above the board while you're working through{' '}
          <span style={kbd}>Review matching faces</span> for a person — confirm or dismiss each
          suggested cluster before it clears.
        </Callout>
      </DocSection>
    </>
  );
}
